/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import isNil from 'lodash.isnil';
import isObject from 'lodash.isobject';
import isString from 'lodash.isstring';
import {error} from '../logger/logger';
import {safeJSONParse} from '../utils/JSONUtils';

// cached local storage supported value (for subsequent invocations).
let _supported: boolean;

/**
 * Provides a convenience method for determining if the user agent implements
 * the HTML5 LocalStorage API.
 *
 * ```typescript
 *   import {hasLocalStorage} from '@ion/core/src/capabilities/storage';
 *
 *    // determine if local storage is available ...
 *   const hasStorage = hasLocalStorage();
 *
 * ```
 *
 * @return true if localStorage available, otherwise, false.
 */
export const hasLocalStorage = (global: object = {}): boolean => {
  if (!_supported) {
    try {
      const provider = Boolean(Object.keys(global).length);
      _supported = provider || 'localStorage' in window;
      return _supported;
    } catch (e) {
      return false;
    }
  }
  return _supported;
};

/**
 * Provides a convenience / utility from which items can be persisted and / or
 * retrieved from localStorage.
 *
 * ```typescript
 *   import {localPersistence} from '@ion/core/src/capabilities/storage';
 *
 *   // persists {"some-id": {"name": 'Eric Feminella'}} to localStorage ...
 *   localPersistence('some-id', 'name', 'Eric Feminella');
 *
 * ```
 *
 * @param itemName The unique id to which keys are persisted.
 * @param key the key of the item to get or set
 * @param val the value to persist to the given key within the
 *        specified itemName. This argument is optional, and, when
 *        omitted, this function behaves as a getter. Likewise, when
 *        provides, this function behaves as a setter.
 *
 */
export const localPersistence = (itemName: string, key: string, val?: any): any => {
  if (hasLocalStorage()) {
    if (isNil(val)) {
      const storage = safeJSONParse(window.localStorage.getItem(itemName)) || {};
      if (!storage) {
        error(`[CalAmp Core] JSON.parse failed to resolve ${itemName} from local storage`);
      }
      return storage[key];
    } else {
      window.localStorage.setItem(
        itemName,
        JSON.stringify({
          [key] : val
        })
      );
    }
  }
};

/**
 * Retrieves the named item from LocalStorage for the given domain and returns
 * the result. If the item can be decoded to a JSON object, the item will be
 * serialized to an object and returned, otherwise, the item value will simply
 * be returned.
 *
 * ```typescript
 *   import {getLocalStorage} from '@ion/core/src/capabilities/storage';
 *
 *    // retrieve the persisted 'username' and 'password' from local storage ...
 *   const username = getLocalStorage('username');
 *   const password = getLocalStorage('password');
 *
 * ```
 *
 * @param   name The name of the item to retrieve from LocalStorage
 *          for the given domain.
 * @returns The serialized item retrieved from LocalStorage as a
 *          serialized JSON object, or the value of the item retrieved from
 *          LocalStorage.
 */
export const getLocalStorage = (name: string): any => {
  if (hasLocalStorage()) {
    const item = window.localStorage.getItem(name);
    if (isString(item)) {
      if (
        item.indexOf('{') === 0 &&
        item.lastIndexOf('}') === item.length - 1
      ) {
        return safeJSONParse(item);
      }
    }
    return item;
  }
};

/**
 * Sets the an entry's value in local storage to the given value.
 *
 * ```typescript
 *   import {getLocalStorage} from '@ion/core/src/capabilities/storage';
 *
 *    // set the persisted 'username' and 'password' to local storage ...
 *   setLocalStorage('username', username);
 *   setLocalStorage('password', password);
 *
 * ```
 *
 * Sets the value for the named item to LocalStorage for the given domain.
 *
 * @param  name The name of the item to retrieve from LocalStorage
 *         for the given domain.
 * @param  value The object which is to be saved for the named item.
 */
export const setLocalStorage = (name: string, value: any): void => {
  if (hasLocalStorage()) {
    const val = isObject(value) ? JSON.stringify(value) : value;
    window.localStorage.setItem(name, val);
  }
};

/**
 * Deletes the entry of the given name / key from local storage.
 *
 * ```typescript
 *   import {deleteLocalStorage} from '@ion/core/src/capabilities/storage';
 *
 *    // delete the persisted 'username' and 'password' to local storage ...
 *   deleteLocalStorage('username');
 *   deleteLocalStorage('password');
 *
 * ```
 *
 * Deletes the value for the named item from LocalStorage for the given domain.
 *
 * @param  name The name of the item to retrieve from LocalStorage
 *         for the given domain.
 */
export const deleteLocalStorage = (name: string): void => {
  if (hasLocalStorage()) {
    window.localStorage.removeItem(name);
  }
};

export default hasLocalStorage;
